import type { CSSProperties } from "react";
import { cn } from "@/lib/utils";

const COURT_WIDTH = 120;
const COURT_HEIGHT = 240;
const COURT_TOP = 24;
const NET_Y = COURT_TOP + COURT_HEIGHT / 2;
const NET_OVERHANG = 10;
const NUMBER_Y = COURT_TOP + COURT_HEIGHT + 26;
const BADGE_Y = COURT_TOP + 176;
const BOOKING_DELAY = "[--plan-delay:1100ms]";

const COURTS = [
	{ booked: false, number: 1, x: 24 },
	{ booked: true, number: 2, x: 180 },
	{ booked: false, number: 3, x: 336 },
	{ booked: false, number: 4, x: 492 },
] as const;

type Court = (typeof COURTS)[number];

const STAGGER = "[--plan-delay:calc(var(--i)*90ms)]";
const LINE = `fill-none stroke-[2.25] stroke-muted-foreground/60 [stroke-dasharray:1] motion-safe:animate-plan-draw ${STAGGER}`;
const FADE = `motion-safe:animate-plan-light ${STAGGER}`;

/**
 * A venue seen from above: four beach courts, the lines drawing in once, then
 * court 2 lights up as booked. At rest (and under reduced motion) it shows the
 * finished plan. Decorative: the pitch beside it says the same thing in words.
 */
export function VenuePlan({ className }: { className?: string }) {
	return (
		<svg
			aria-hidden="true"
			className={cn("aspect-[616/300]", className)}
			preserveAspectRatio="xMinYMid meet"
			viewBox="10 0 616 300"
		>
			{COURTS.map((court, index) => (
				<CourtPlan court={court} index={index} key={court.number} />
			))}
			{COURTS.filter((court) => court.booked).map((court) => (
				<BookedCourt court={court} key={court.number} />
			))}
		</svg>
	);
}

function CourtPlan({ court, index }: { court: Court; index: number }) {
	const { x, number, booked } = court;
	const netEnds = [x - NET_OVERHANG, x + COURT_WIDTH + NET_OVERHANG];

	return (
		<g style={{ "--i": index } as CSSProperties}>
			<rect
				className={cn("fill-white/5", FADE)}
				height={COURT_HEIGHT}
				rx={3}
				width={COURT_WIDTH}
				x={x}
				y={COURT_TOP}
			/>
			<path
				className={LINE}
				d={`M${x} ${COURT_TOP}h${COURT_WIDTH}v${COURT_HEIGHT}h${-COURT_WIDTH}z`}
				pathLength={1}
			/>
			<path className={LINE} d={`M${netEnds[0]} ${NET_Y}H${netEnds[1]}`} pathLength={1} />
			<g className={cn("fill-muted-foreground/60", FADE)}>
				{netEnds.map((cx) => (
					<circle cx={cx} cy={NET_Y} key={cx} r={3.5} />
				))}
			</g>
			{booked ? null : (
				<text
					className={cn("fill-muted-foreground font-medium text-[15px] max-sm:hidden", FADE)}
					textAnchor="middle"
					x={x + COURT_WIDTH / 2}
					y={NUMBER_Y}
				>
					{number}
				</text>
			)}
		</g>
	);
}

function BookedCourt({ court }: { court: Court }) {
	const { x, number } = court;
	const left = x - NET_OVERHANG;
	const right = x + COURT_WIDTH + NET_OVERHANG;

	return (
		<>
			<g className={`motion-safe:animate-plan-light ${BOOKING_DELAY}`}>
				<rect
					className="fill-verdigris-bright/12 stroke-[2.25] stroke-verdigris-bright"
					height={COURT_HEIGHT}
					rx={3}
					width={COURT_WIDTH}
					x={x}
					y={COURT_TOP}
				/>
				<path
					className="fill-none stroke-[2.25] stroke-verdigris-bright"
					d={`M${left} ${NET_Y}H${right}`}
				/>
				<circle className="fill-verdigris-bright" cx={left} cy={NET_Y} r={3.5} />
				<circle className="fill-verdigris-bright" cx={right} cy={NET_Y} r={3.5} />
				<text
					className="fill-verdigris-bright font-medium text-[15px] max-sm:hidden"
					textAnchor="middle"
					x={x + COURT_WIDTH / 2}
					y={NUMBER_Y}
				>
					{number}
				</text>
			</g>
			<g transform={`translate(${x + COURT_WIDTH / 2} ${BADGE_Y})`}>
				<g
					className={
						"[--plan-delay:1350ms] [transform-box:fill-box] [transform-origin:center] motion-safe:animate-plan-pop"
					}
				>
					<rect className="fill-verdigris-bright" height={26} rx={13} width={64} x={-32} y={-13} />
					<path
						className="fill-none stroke-[2.5] stroke-ink [stroke-linecap:round] [stroke-linejoin:round]"
						d="M-7 .5l4.5 4.5 9.5-9.5"
					/>
				</g>
			</g>
		</>
	);
}
